/**
 * BreakdownChartCard — the level's bars, with the gap between them as the margin and the
 * GM% line over the top. Shared by the two facts that drill this way: the order book (what
 * was ordered, and what it would cost) and margin (what was billed, and what it cost).
 *
 * They asked for the identical card in identical terms apart from one word, so this holds it
 * once. `measure` is that word — the order book charts VALUE, margin charts REVENUE — and it
 * matters enough to say out loud, because on the margin fact revenue includes the invoice's
 * apportioned carriage and value would read as goods alone.
 *
 * When there are more rows than the plot can carry, the caption names exactly what is off
 * it. The table below still carries every row and the sum check still proves the whole set,
 * so this is a legibility limit, never a silent truncation (constitution §3).
 */

import React from 'react'
import MarginBreakdownChart from './charts/MarginBreakdownChart'
import { capBars, capRecent, toBars, type CostColumns } from '../../viewmodels/orderBook'
import { formatCurrency } from '../../utils/formatters'
import type { DrillRow } from '../../types/metrics'

/** What fell off the plot, named. A legibility limit that does not say what it dropped is
 * indistinguishable from a silent truncation, which is the thing §3 forbids. */
function OffChart({ shown, total, hidden, measure }: {
  shown: number
  total: number
  hidden: { count: number; value: number }
  /** What the chart kept the largest BY, or null on a time axis, which keeps the latest. */
  measure: string | null
}): React.JSX.Element {
  return (
    <p className="border-t border-line2 px-4 py-2 text-[11.5px] text-mut2">
      {measure === null
        ? `Charting the most recent ${shown} of ${total} months.`
        : `Charting the largest ${shown} of ${total} by ${measure}.`}{' '}
      The other {hidden.count} are{' '}
      {formatCurrency(hidden.value, '£', 0)} and are in the table below — the cards and the sum check
      cover every row, charted or not.
    </p>
  )
}

export default function BreakdownChartCard({
  data, columns, measure, onBarClick,
}: {
  /** Any served breakdown payload — both facts carry these three fields. */
  data: { rows: DrillRow[]; descend_key: string | null; level: string }
  /** Which columns hold the cost and the GM. The order book has a pair per basis; the margin
   * fact has one pair, because its basis is a property of the row and not a choice. */
  columns: CostColumns
  /** What the bars are the height of, in the words the surface uses for it. */
  measure: 'value' | 'revenue'
  onBarClick?: (row: DrillRow) => void
}): React.JSX.Element {
  // A time axis truncates by RECENCY and a dimension by VALUE — see capRecent. The caption
  // below has to say which, or "the largest 25 of 67" reads as a claim about the wrong thing.
  const time = data.level === 'month'
  const { bars, hidden } = (time ? capRecent : capBars)(toBars(data.rows, data.descend_key, columns))
  const grain = data.descend_key?.toLowerCase() ?? data.level
  const title = measure === 'revenue' ? 'Revenue' : 'Value'
  // Read the split off the BARS rather than off the basis. A cost decomposes when its five
  // components tie to it (viewmodels/orderBook componentsOf), which is a fact about the rows
  // in front of us — the order book's part standard never does, the margin fact's cost always
  // does, and a basis test would have to encode both and go stale when one changes.
  const splits = bars.some((bar) => bar.components !== null)
  return (
    <div className="rounded-xl border border-line bg-panel">
      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-line2 text-[13px] font-semibold text-ink">
        {title} vs cost by {grain}
        <span className="font-mono text-[10px] font-normal text-mut2">
          the gap between the bars is the margin · GM% line
          {splits ? ' · cost split by what it is made of' : ''}
          {onBarClick ? ' · click to drill' : ''}
        </span>
      </div>
      <div className="px-2 pt-3">
        <MarginBreakdownChart bars={bars} label={`By ${grain}`} splitCost onBarClick={onBarClick} />
      </div>
      {hidden && <OffChart shown={bars.length} total={data.rows.length} hidden={hidden} measure={measure} />}
    </div>
  )
}
